// UsersAdminPage — super-admin only.
//   /dispatch/admin/users
//
// User search + profile edit with mandatory reason. Every edit is audit-logged
// via /api/admin/users/{id} PATCH and the immutable user_audit_log collection.
// Editable fields: name, phone, address, region, role, active.
// Rate limiting will be added to this endpoint in Phase 7.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Search, User as UserIcon, Save, Shield, Clock, History, Power, Trash2, Phone, Mail, MessageSquare, MessageCircle, UserPlus, CalendarDays, Package, Smartphone, Bell, BellOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MiniSparkline } from '../../components/MiniSparkline';
import { AccountRecoveryControls } from '../../components/AccountRecoveryControls';
import { Navbar } from '../../components/Navbar';
import { openGlobalUserSearch } from '../../components/GlobalUserSearch';
import { pushRecentlyViewedCustomer } from '../../components/RecentlyViewedCustomersStrip';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Feb 2026 — Root-cause fix for the "[object Object]" address bug.
// Customers registered via AddressAutocomplete store a structured object
// {line1, city, state, postcode, apt, lat, lng, formatted} in users.address.
// If we drop that object straight into the raw <Input value={...}> on the
// SA drawer, React coerces it via String(obj) which produces literal
// "[object Object]" in the field. The first keystroke or blur then persists
// that sentinel string to Mongo. Coerce to a readable string on load so the
// operator sees + edits the flat text form.
function addressToString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const apt = value.apt ? `Apt ${value.apt}` : '';
    const head = [value.line1, apt].filter(Boolean).join(', ');
    const tail = [value.city, value.state, value.postcode].filter(Boolean).join(', ');
    return [head, tail].filter(Boolean).join(', ') || value.formatted || '';
  }
  return String(value);
}

const ROLE_OPTIONS = ['customer', 'driver', 'dispatcher'];
const ROLE_COLORS = {
  customer: '#39FF14',
  driver: '#00F0FF',
  dispatcher: '#FFA500',
  super_admin: '#FF9F0A',
};

// Registry of pages that deep-link into User Management via `?openUser=ID&from=KEY`.
// Add a new entry to expose a smart "← BACK TO X" header for that source.
// `url` may be a hash route if the source is a drawer on the dashboard.
const DEEP_LINK_SOURCES = {
  'data-quality':    { label: '← BACK TO DATA QUALITY',    url: '/dispatch/admin/data-quality' },
  'active-carts':    { label: '← BACK TO ACTIVE CARTS',    url: '/dispatch' },
  'activity-feed':   { label: '← BACK TO ACTIVITY FEED',   url: '/dispatch' },
  'leaderboards':    { label: '← BACK TO LEADERBOARDS',    url: '/dispatch/admin/leaderboards' },
  'founders-thanks': { label: '← BACK TO FOUNDERS THANKS', url: '/dispatch/admin/founders-thanks' },
  'dispatch':        { label: '← BACK TO DISPATCH',        url: '/dispatch' },
  'failed-orders':   { label: '← BACK TO FAILED ORDERS',   url: '/dispatch/admin/failed-orders' },
  'points-admin':    { label: '← BACK TO POINTS ADMIN',    url: '/dispatch/admin/points' },
  'points-watch':    { label: '← BACK TO POINTS WATCH',    url: '/dispatch/admin/points-watch' },
  'push-health':     { label: '← BACK TO PUSH HEALTH',     url: '/dispatch/admin/push-health' },
  'at-risk':         { label: '← BACK TO AT-RISK',         url: '/dispatch/admin/at-risk-customers' },
  'login-stats':     { label: '← BACK TO LOGIN STATS',     url: '/dispatch/admin/login-stats' },
  'email-health':    { label: '← BACK TO EMAIL HEALTH',    url: '/dispatch/admin/email-health' },
  'daily-brief':     { label: '← BACK TO INBOX',           url: '/dispatch?tab=inbox' },
};

export function UsersAdminPage() {
  const { user } = useAuth();
  // Phase 6.0.2 — Dispatchers may also view this page (read-only) so the
  // activity-feed click-through to a customer profile works. Edit affordances
  // (PATCH role/region, points adjust, hard-delete, code rotate, password
  // reset, etc.) are gated by `canEdit` further down so dispatchers see a
  // clean read-only profile.
  const isSuper = user?.role === 'super_admin';
  const canEdit = isSuper;
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [joinedWithin, setJoinedWithin] = useState('all'); // Phase 6 — onboarding-conversion filter
  const [joinedFrom, setJoinedFrom] = useState(''); // Phase 6 — custom date range (inclusive)
  const [joinedTo, setJoinedTo] = useState('');     // Phase 6 — custom date range (inclusive)
  const [tierFilter, setTierFilter] = useState('all'); // Phase 5.9.16 — LTV tier chip
  const [tierMap, setTierMap] = useState({});          // { user_id: 'whale'|'vip'|'regular'|'new' }
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [users, setUsers] = useState([]);
  const [cohortStats, setCohortStats] = useState(null); // Phase 6 — cohort revenue tile
  const [loadingList, setLoadingList] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Phase 5.9.17 — Typeahead dropdown global search
  // Independent of the main list; fires a debounced lightweight query while the
  // operator types so they can jump straight to a user without applying filters.
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const [typeaheadLoading, setTypeaheadLoading] = useState(false);
  const [typeaheadActiveIdx, setTypeaheadActiveIdx] = useState(-1);
  const typeaheadRef = useRef(null);
  const typeaheadDebounceRef = useRef(null);
  const typeaheadAbortRef = useRef(null);
  const [regions, setRegions] = useState([]);
  // Phase 7-lite — hard-delete confirmation flow
  const [hardDeletePreview, setHardDeletePreview] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);

  const requestHardDelete = async () => {
    if (!selectedUser) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/users/${selectedUser.id}/hard-delete/preview`,
        { withCredentials: true }
      );
      setHardDeletePreview(data);
    } catch (e) {
      toast.error('Preview failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const confirmHardDelete = async () => {
    if (!selectedUser || !hardDeletePreview) return;
    setHardDeleting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/hard-delete`,
        {},
        { withCredentials: true }
      );
      toast.success(`Permanently deleted ${data.deleted_email}`);
      setHardDeletePreview(null);
      setSelectedUser(null);
      setUserDetail(null);
      // Refresh search list
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setHardDeleting(false);
    }
  };

  useEffect(() => {
    async function loadRegions() {
      try {
        const { data } = await axios.get(`${API_URL}/api/regions`, { withCredentials: true });
        setRegions(data.regions || []);
      } catch {}
    }
    loadRegions();
  }, []);

  const search = async (overrides = {}) => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams();
      const effQ = overrides.q !== undefined ? overrides.q : q;
      const effRole = overrides.roleFilter !== undefined ? overrides.roleFilter : roleFilter;
      const effRegion = overrides.regionFilter !== undefined ? overrides.regionFilter : regionFilter;
      const effJoined = overrides.joinedWithin !== undefined ? overrides.joinedWithin : joinedWithin;
      const effFrom = overrides.joinedFrom !== undefined ? overrides.joinedFrom : joinedFrom;
      const effTo = overrides.joinedTo !== undefined ? overrides.joinedTo : joinedTo;
      const effTier = overrides.tierFilter !== undefined ? overrides.tierFilter : tierFilter;
      if (effQ.trim()) params.set('q', effQ.trim());
      if (effRole !== 'all') params.set('role', effRole);
      if (effRegion !== 'all') params.set('region', effRegion);
      if (effTier !== 'all') params.set('tier', effTier);
      // Custom range takes precedence over preset (matches backend behavior)
      if (effFrom || effTo) {
        if (effFrom) params.set('joined_from', effFrom);
        if (effTo) params.set('joined_to', effTo);
      } else if (effJoined !== 'all') {
        params.set('joined_within', effJoined);
      }
      params.set('limit', '200');
      const { data } = await axios.get(`${API_URL}/api/admin/users?${params}`, { withCredentials: true });
      setUsers(data.users || []);
      setCohortStats(data.cohort_stats || null);
      setTierMap(data.tier_map || {});
    } catch (e) {
      toast.error('Search failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { search(); /* initial */ /* eslint-disable-next-line */ }, []);

  // Phase 5.9.17 — Typeahead: debounced fetch (300ms) any time `q` changes.
  // Skips when q is empty or below 2 chars to avoid hammering the API.
  // Aborts in-flight requests on new input so stale results never overwrite fresh ones.
  useEffect(() => {
    if (typeaheadDebounceRef.current) clearTimeout(typeaheadDebounceRef.current);
    if (typeaheadAbortRef.current) typeaheadAbortRef.current.abort();
    const term = q.trim();
    // All state updates deferred into the timer callback so the effect body
    // contains no synchronous setState (avoids react-hooks/set-state-in-effect).
    typeaheadDebounceRef.current = setTimeout(async () => {
      if (term.length < 2) {
        setTypeaheadResults([]);
        setTypeaheadOpen(false);
        setTypeaheadLoading(false);
        return;
      }
      setTypeaheadLoading(true);
      const ctrl = new AbortController();
      typeaheadAbortRef.current = ctrl;
      try {
        const params = new URLSearchParams({ q: term, limit: '8' });
        const { data } = await axios.get(
          `${API_URL}/api/admin/users?${params}`,
          { withCredentials: true, signal: ctrl.signal },
        );
        setTypeaheadResults((data.users || []).slice(0, 8));
        setTypeaheadOpen(true);
        setTypeaheadActiveIdx(-1);
      } catch (e) {
        if (!axios.isCancel(e) && e.name !== 'CanceledError') {
          // silent fail — typeahead errors shouldn't toast spam
          console.debug('typeahead error:', e);
        }
      } finally {
        setTypeaheadLoading(false);
      }
    }, 300);
    return () => {
      if (typeaheadDebounceRef.current) clearTimeout(typeaheadDebounceRef.current);
    };
  }, [q]);

  // Close typeahead on outside click + Escape key
  useEffect(() => {
    const onClick = (e) => {
      if (typeaheadRef.current && !typeaheadRef.current.contains(e.target)) {
        setTypeaheadOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setTypeaheadOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const pickTypeaheadResult = (u) => {
    setTypeaheadOpen(false);
    setQ('');
    setTypeaheadResults([]);
    setTypeaheadActiveIdx(-1);
    openUser(u);
  };

  const openUser = async (u) => {
    setSelectedUser(u);
    setUserDetail(null);
    setReason('');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users/${u.id}`, { withCredentials: true });
      setUserDetail(data);
      setDraft({
        name: data.user.name || '',
        phone: data.user.phone || '',
        // Feb 2026 hotfix — coerce structured-object addresses to a display
        // string before dropping into the raw <Input>. Prevents React from
        // stringifying the object to "[object Object]" in the DOM (which
        // would then persist on next save). See addressToString above.
        address: addressToString(data.user.address),
        region: data.user.region || '',
        role: data.user.role || 'customer',
        active: data.user.active !== false,
        // Feb 2026 Annual Tier — pre-fill the executive override control
        // with the current lock state so the operator sees exactly what's
        // pinned before they touch anything. `""` means "auto" (no lock).
        locked_tier: data.user.locked_tier || '',
        locked_tier_until: data.user.tier_lock_until || '',
      });
      // Feb 2026 (Slice 2 · step 4) — stamp this profile onto the operator's
      // Recently-Viewed strip so re-hopping is one tap on the Ops home.
      pushRecentlyViewedCustomer(data.user);
    } catch (e) {
      // Phase 6 Tier 1 — surface the real error instead of swallowing it.
      // Previously this said "Could not load user detail" with no actionable info.
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      const code = e?.response?.status;
      toast.error(`Could not load user${code ? ` (HTTP ${code})` : ''}: ${detail}`);
    }
  };

  // Phase 5.9.29 — `?openUser={id}` deep-link support. Powered by the Activity
  // Feed customer-name click (and any future deep-link source — e.g. the
  // upcoming Process Health Dashboard). Resolves the user to a minimal shape
  // (just `{id}`) and lets `openUser` fetch the full record from the API.
  const [searchParams, setSearchParams] = useSearchParams();
  const openUserDeepLinkRef = useRef(null);
  // Track the deep-link source page so we can render a smart "← BACK TO X"
  // header instead of the hard-coded "/dispatch" link. Add new sources to
  // DEEP_LINK_SOURCES below — they're then auto-rendered with the right label
  // + return URL without touching the JSX.
  const [deepLinkFrom, setDeepLinkFrom] = useState(null);
  useEffect(() => {
    const uid = searchParams.get('openUser');
    if (!uid || openUserDeepLinkRef.current === uid) return;
    openUserDeepLinkRef.current = uid;
    const from = searchParams.get('from');
    if (from) setDeepLinkFrom(from);
    openUser({ id: uid, name: '', email: '' });
    // Clear the param so refresh/back doesn't re-open.
    const next = new URLSearchParams(searchParams);
    next.delete('openUser');
    next.delete('from');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Mobile UX (Phase 6 Tier 1): when a user is selected, hide the list on
  // small screens and show only the detail panel so the operator never has to
  // scroll through 96 rows to find what they tapped. Tapping "← BACK TO LIST"
  // returns to the list view. On lg+ screens both panels stay visible.
  const backToList = () => {
    setSelectedUser(null);
    setUserDetail(null);
    setReason('');
    setHardDeletePreview(null);
  };

  const diffFields = useMemo(() => {
    if (!userDetail) return [];
    const out = [];
    const u = userDetail.user;
    for (const k of ['name', 'phone', 'address', 'region', 'role', 'active']) {
      const oldVal = k === 'active' ? (u.active !== false) : (u[k] || (k === 'role' ? 'customer' : ''));
      const newVal = draft[k];
      if (oldVal !== newVal) out.push({ field: k, from: oldVal, to: newVal });
    }
    // Feb 2026 Annual Tier — executive tier-override diff. `locked_tier`
    // maps 1:1 to backend field; `locked_tier_until` is the frontend name
    // that the backend translates to `tier_lock_until` at write time.
    const currentLockedTier = u.locked_tier || '';
    const currentLockedUntil = u.tier_lock_until || '';
    if ((draft.locked_tier || '') !== currentLockedTier) {
      out.push({ field: 'locked_tier', from: currentLockedTier, to: draft.locked_tier || '' });
    }
    if ((draft.locked_tier_until || '') !== currentLockedUntil && draft.locked_tier) {
      out.push({ field: 'locked_tier_until', from: currentLockedUntil, to: draft.locked_tier_until || '' });
    }
    return out;
  }, [userDetail, draft]);

  const cashappChanged = useMemo(() => {
    if (!userDetail) return false;
    if (draft.cashapp_handle === undefined) return false;  // not touched
    const current = userDetail.user.cashapp_handle || '';
    return (draft.cashapp_handle || '') !== current;
  }, [userDetail, draft.cashapp_handle]);

  const handleSave = async () => {
    if (!reason.trim() && (diffFields.length > 0)) {
      toast.error('Please provide a reason for this change');
      return;
    }
    if (diffFields.length === 0 && !cashappChanged) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      // 1. Standard fields via existing admin PATCH (audit logged)
      let updatedUser = userDetail.user;
      let auditList = userDetail.audit || [];
      if (diffFields.length > 0) {
        const body = { reason: reason.trim() };
        diffFields.forEach(c => {
          body[c.field] = c.to === '' ? null : c.to;
        });
        const { data } = await axios.patch(
          `${API_URL}/api/admin/users/${selectedUser.id}`,
          body,
          { withCredentials: true }
        );
        updatedUser = data.user;
        auditList = [data.audit, ...auditList];
      }
      // 2. Cashapp override via dedicated endpoint (audit logged separately)
      if (cashappChanged && (userDetail?.user?.role === 'driver' || draft.role === 'driver')) {
        const handle = (draft.cashapp_handle || '').trim();
        const { data } = await axios.patch(
          `${API_URL}/api/admin/users/${selectedUser.id}/cashapp`,
          { cashapp_handle: handle },
          { withCredentials: true }
        );
        updatedUser = { ...updatedUser, cashapp_handle: data.cashapp_handle };
      }
      toast.success('User updated — audit entry recorded');
      setUserDetail(prev => ({ ...prev, user: updatedUser, audit: auditList }));
      setReason('');
      setDraft(prev => ({ ...prev, cashapp_handle: undefined }));
      setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!user || (user.role !== 'super_admin' && user.role !== 'dispatcher')) {
    return (
      <div className="min-h-screen bg-[#050003] text-[#39FF14] font-mono p-6">
        <p className="text-[#6B8E73]">// Operator only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003] text-[#39FF14] font-mono" data-testid="users-admin-page">
      <Navbar />
      <div
        className="border-b border-[#2A0F3D] sticky top-0 z-10 bg-[#050003]/95 backdrop-blur dispatch-sticky-top"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          {deepLinkFrom && DEEP_LINK_SOURCES[deepLinkFrom] ? (
            <a
              href={DEEP_LINK_SOURCES[deepLinkFrom].url}
              className="text-[#6B8E73] hover:text-[#39FF14] text-xs uppercase"
              data-testid="user-back-to-source"
            >
              {DEEP_LINK_SOURCES[deepLinkFrom].label}
            </a>
          ) : (
            <a href="/dispatch" className="text-[#6B8E73] hover:text-[#39FF14] text-xs uppercase" data-testid="user-back-to-dispatch">← BACK</a>
          )}
          <span className="text-[#FF9F0A] font-bold tracking-wider flex items-center gap-2">
            <Shield className="w-4 h-4" /> USER_MANAGEMENT_
          </span>
          <Badge className="bg-[rgba(255,159,10,0.15)] text-[#FF9F0A] border border-[#FF9F0A] rounded-none font-mono text-[10px]">
            SUPER-ADMIN ONLY
          </Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 p-4">
        {/* ========== LEFT: Search + List ========== */}
        {/* Phase 6 Tier 1 — on mobile, hide the list once a user is selected so
            the operator never has to scroll past 96 rows to reach the detail. */}
        <div className={`space-y-4 ${selectedUser ? 'hidden lg:block' : ''}`}>
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="user-search">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1" ref={typeaheadRef}>
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B6B52] z-10" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onFocus={() => { if (typeaheadResults.length > 0) setTypeaheadOpen(true); }}
                  onKeyDown={(e) => {
                    // Typeahead navigation: arrow keys + Enter to pick highlighted row.
                    if (typeaheadOpen && typeaheadResults.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setTypeaheadActiveIdx((i) => Math.min(i + 1, typeaheadResults.length - 1));
                        return;
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setTypeaheadActiveIdx((i) => Math.max(i - 1, -1));
                        return;
                      }
                      if (e.key === 'Enter' && typeaheadActiveIdx >= 0) {
                        e.preventDefault();
                        pickTypeaheadResult(typeaheadResults[typeaheadActiveIdx]);
                        return;
                      }
                    }
                    if (e.key === 'Enter') search();
                  }}
                  placeholder="email or name..."
                  className="pl-8 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                  data-testid="user-search-input"
                  autoComplete="off"
                />

                {/* Phase 5.9.17 — Typeahead dropdown — appears under the input
                    while the operator types. Click or arrow-key + Enter to
                    jump straight to a user without applying filters. */}
                {typeaheadOpen && (typeaheadResults.length > 0 || typeaheadLoading) && (
                  <div
                    className="absolute z-30 left-0 right-0 top-full mt-1 bg-[#050003] border border-[#FF9F0A]/60 rounded-none shadow-2xl max-h-80 overflow-y-auto"
                    data-testid="user-typeahead-dropdown"
                  >
                    {typeaheadLoading && (
                      <div className="px-3 py-2 text-[10px] text-[#6B8E73] font-mono uppercase">
                        <Clock className="inline w-3 h-3 mr-1 animate-spin" /> Searching…
                      </div>
                    )}
                    {!typeaheadLoading && typeaheadResults.map((u, idx) => {
                      const initials = (u.name || u.email || '?')
                        .split(/\s+|@/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase() || '')
                        .join('') || '?';
                      const active = idx === typeaheadActiveIdx;
                      const color = ROLE_COLORS[u.role] || '#A3C7A8';
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => pickTypeaheadResult(u)}
                          onMouseEnter={() => setTypeaheadActiveIdx(idx)}
                          className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-[#2A0F3D]/40 last:border-b-0 transition-colors ${
                            active ? 'bg-[#FF9F0A]/15' : 'hover:bg-[#2A0F3D]/30'
                          }`}
                          data-testid={`user-typeahead-row-${u.id}`}
                        >
                          {/* Avatar — initials in role-colored circle */}
                          <div
                            className="w-8 h-8 shrink-0 flex items-center justify-center font-mono text-xs font-bold border"
                            style={{ borderColor: color, color }}
                          >
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-[#39FF14] font-mono text-xs truncate">
                                {u.name || '—'}
                              </span>
                              {u.region && (
                                <span className="text-[#6B8E73] font-mono text-[9px] uppercase">
                                  {u.region}
                                </span>
                              )}
                              {!u.active && (
                                <span className="text-[#FF3B30] font-mono text-[9px] uppercase">INACTIVE</span>
                              )}
                            </div>
                            <div className="text-[#A3C7A8]/70 font-mono text-[10px] truncate">{u.email}</div>
                          </div>
                          <span
                            className="font-mono text-[9px] uppercase shrink-0 tracking-wider"
                            style={{ color }}
                          >
                            {u.role}
                          </span>
                        </button>
                      );
                    })}
                    {!typeaheadLoading && typeaheadResults.length > 0 && (
                      <div className="px-3 py-1.5 text-[9px] text-[#6B8E73] font-mono uppercase border-t border-[#2A0F3D]">
                        ↑↓ navigate · enter select · esc close
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button onClick={search} disabled={loadingList}
                className="bg-[#FF9F0A] text-[#050003] hover:bg-[#FFAF2A] rounded-none" data-testid="user-search-btn">
                SEARCH
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-xs" data-testid="user-role-filter">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                  <SelectItem value="all" className="text-[#39FF14] font-mono">ALL_ROLES</SelectItem>
                  <SelectItem value="customer" className="text-[#39FF14] font-mono">CUSTOMER</SelectItem>
                  <SelectItem value="driver" className="text-[#39FF14] font-mono">DRIVER</SelectItem>
                  <SelectItem value="dispatcher" className="text-[#39FF14] font-mono">DISPATCHER</SelectItem>
                  <SelectItem value="super_admin" className="text-[#39FF14] font-mono">SUPER_ADMIN</SelectItem>
                </SelectContent>
              </Select>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-xs" data-testid="user-region-filter">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                  <SelectItem value="all" className="text-[#39FF14] font-mono">ALL_REGIONS</SelectItem>
                  {regions.map(r => (
                    <SelectItem key={r.code} value={r.code} className="text-[#39FF14] font-mono">{r.code.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Phase 6 Tier 1 — onboarding-conversion filter for marketing/campaign review */}
            <div className="mt-3" data-testid="user-joined-within-row">
              <p className="text-[#6B8E73] font-mono text-[10px] uppercase mb-1">// Joined within</p>
              <div className="flex flex-wrap gap-1">
                {[
                  { v: 'all', label: 'ALL TIME' },
                  { v: '1d',  label: 'LAST 1d' },
                  { v: '7d',  label: 'LAST 7d' },
                  { v: '30d', label: 'LAST 30d' },
                  { v: '90d', label: 'LAST 90d' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => {
                      setJoinedWithin(opt.v);
                      setJoinedFrom(''); setJoinedTo(''); setShowCustomDates(false);
                      search({ joinedWithin: opt.v, joinedFrom: '', joinedTo: '' });
                    }}
                    className={`font-mono text-[10px] uppercase px-2 py-1 border rounded-none ${
                      joinedWithin === opt.v && !joinedFrom && !joinedTo
                        ? 'border-[#FF9F0A] text-[#FF9F0A] bg-[#FF9F0A]/10'
                        : 'border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF9F0A]/40'
                    }`}
                    data-testid={`user-joined-within-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  onClick={() => setShowCustomDates(v => !v)}
                  className={`font-mono text-[10px] uppercase px-2 py-1 border rounded-none ${
                    (joinedFrom || joinedTo)
                      ? 'border-[#FF9F0A] text-[#FF9F0A] bg-[#FF9F0A]/10'
                      : 'border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF9F0A]/40'
                  }`}
                  data-testid="user-joined-within-custom-toggle"
                >
                  CUSTOM…
                </button>
              </div>
              {showCustomDates && (
                <div className="mt-2 p-2 bg-[#050003] border border-[#FF9F0A]/30 space-y-2" data-testid="user-joined-custom-row">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[9px] uppercase">From</Label>
                      <Input
                        type="date"
                        value={joinedFrom}
                        onChange={(e) => setJoinedFrom(e.target.value)}
                        className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono text-xs rounded-none mt-1"
                        data-testid="user-joined-from"
                      />
                    </div>
                    <div>
                      <Label className="text-[#6B8E73] font-mono text-[9px] uppercase">To</Label>
                      <Input
                        type="date"
                        value={joinedTo}
                        onChange={(e) => setJoinedTo(e.target.value)}
                        className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono text-xs rounded-none mt-1"
                        data-testid="user-joined-to"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setJoinedWithin('all');
                        search({ joinedWithin: 'all', joinedFrom, joinedTo });
                      }}
                      disabled={!joinedFrom && !joinedTo}
                      className="bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/80 font-mono text-xs rounded-none flex-1"
                      data-testid="user-joined-apply-btn"
                    >
                      APPLY RANGE
                    </Button>
                    {(joinedFrom || joinedTo) && (
                      <Button
                        onClick={() => {
                          setJoinedFrom(''); setJoinedTo('');
                          search({ joinedFrom: '', joinedTo: '' });
                        }}
                        variant="outline"
                        className="border-[#6B8E73] text-[#6B8E73] hover:bg-[#6B8E73]/10 font-mono text-xs rounded-none"
                        data-testid="user-joined-clear-btn"
                      >
                        CLEAR
                      </Button>
                    )}
                  </div>
                  <p className="font-mono text-[9px] text-[#4B6B52]">
                    // YYYY-MM-DD. Both dates inclusive. Leave one blank for open-ended range.
                  </p>
                </div>
              )}
            </div>

            {/* Phase 5.9.16 — LTV tier filter chips. Aggregates non-cancelled
                spend per user and filters: ONYX ≥$1K, PLATINUM ≥$300, GOLD ≥$50,
                SILVER <$50. Useful for "let me see only my top spenders". */}
            {/* Feb 2026 Annual Tier — filter chips now key on annual delivered
                order count (not lifetime spend). Backend accepts both old and
                new keys via the LEGACY_TIER_ALIASES translation, so a bookmark
                using ?tier=whale still works while callers migrate. */}
            <div className="mt-3" data-testid="user-tier-filter-row">
              <p className="text-[#6B8E73] font-mono text-[10px] uppercase mb-1">// annual tier (YTD orders)</p>
              <div className="flex flex-wrap gap-1">
                {[
                  { v: 'all',      label: 'ALL TIERS',      color: '#6B8E73' },
                  { v: 'onyx',     label: 'ONYX 50+',       color: '#BF5AF2' },
                  { v: 'platinum', label: 'PLATINUM 25+',   color: '#E5E4E2' },
                  { v: 'gold',     label: 'GOLD 10+',       color: '#FFD700' },
                  { v: 'silver',   label: 'SILVER 3+',      color: '#C0C0C0' },
                  { v: 'standard', label: 'NEW 0-2',        color: '#8E8E93' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => {
                      setTierFilter(opt.v);
                      search({ tierFilter: opt.v });
                    }}
                    className={`font-mono text-[10px] uppercase px-2 py-1 border rounded-none ${
                      tierFilter === opt.v
                        ? 'bg-black/30'
                        : 'border-[#2A0F3D] text-[#6B8E73] hover:border-current'
                    }`}
                    style={tierFilter === opt.v ? { color: opt.color, borderColor: opt.color } : {}}
                    data-testid={`user-tier-filter-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Phase 6 Tier 1 (Feb 2026) — Cohort Revenue tile. Reflects the
              currently-active filters so the operator can immediately see
              "did my last marketing push pay off?" without leaving the page. */}
          {cohortStats && cohortStats.cohort_size > 0 && (
            <div className="bg-[#0C0612] border border-[#39FF14]/40" data-testid="cohort-stats-tile">
              <div className="border-b border-[#39FF14]/30 p-3 text-xs uppercase tracking-wider text-[#39FF14] flex items-center justify-between">
                <span>// COHORT REVENUE</span>
                <span className="text-[#6B8E73] text-[10px] normal-case">
                  {cohortStats.cohort_size} user{cohortStats.cohort_size === 1 ? '' : 's'} in cohort
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#39FF14]/10">
                <div className="bg-[#0C0612] p-3" data-testid="cohort-revenue">
                  <p className="font-mono text-[10px] text-[#6B8E73] uppercase">Revenue</p>
                  <p className="font-mono text-xl text-[#39FF14] font-bold mt-1">
                    ${cohortStats.revenue_delivered.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <p className="font-mono text-[9px] text-[#4B6B52] mt-1">// {cohortStats.orders_delivered} delivered</p>
                </div>
                <div className="bg-[#0C0612] p-3" data-testid="cohort-conversion">
                  <p className="font-mono text-[10px] text-[#6B8E73] uppercase">Conversion</p>
                  <p className="font-mono text-xl text-[#FF9F0A] font-bold mt-1">
                    {(cohortStats.conversion_rate * 100).toFixed(1)}%
                  </p>
                  <p className="font-mono text-[9px] text-[#4B6B52] mt-1">// {cohortStats.customers_with_order}/{cohortStats.cohort_size} ordered</p>
                </div>
                <div className="bg-[#0C0612] p-3" data-testid="cohort-aov">
                  <p className="font-mono text-[10px] text-[#6B8E73] uppercase">Avg Order</p>
                  <p className="font-mono text-xl text-[#00F0FF] font-bold mt-1">
                    ${Number(cohortStats.avg_order_value ?? 0).toFixed(2)}
                  </p>
                  <p className="font-mono text-[9px] text-[#4B6B52] mt-1">// delivered AOV</p>
                </div>
                <div className="bg-[#0C0612] p-3" data-testid="cohort-orders-total">
                  <p className="font-mono text-[10px] text-[#6B8E73] uppercase">Orders Placed</p>
                  <p className="font-mono text-xl text-[#BF5AF2] font-bold mt-1">
                    {cohortStats.orders_total}
                  </p>
                  <p className="font-mono text-[9px] text-[#4B6B52] mt-1">// excl. cancelled</p>
                </div>
              </div>
              {/* Phase 6 Tier 1 — Daily sparklines for the active cohort window */}
              {cohortStats.sparkline && cohortStats.sparkline.buckets && cohortStats.sparkline.buckets.length > 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#39FF14]/10" data-testid="cohort-sparkline">
                  <div className="bg-[#0C0612] p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-mono text-[10px] text-[#6B8E73] uppercase">// Daily signups</p>
                      <p className="font-mono text-[9px] text-[#4B6B52]">
                        {cohortStats.sparkline.buckets.length}d window
                      </p>
                    </div>
                    <MiniSparkline
                      data={cohortStats.sparkline.buckets.map(b => b.signups)}
                      color="#FF9F0A"
                      height={36}
                      ariaLabel="Daily signups"
                    />
                    <div className="flex justify-between mt-1 font-mono text-[9px] text-[#4B6B52]">
                      <span>{cohortStats.sparkline.buckets[0]?.date}</span>
                      <span>peak {Math.max(...cohortStats.sparkline.buckets.map(b => b.signups), 0)}/day</span>
                      <span>{cohortStats.sparkline.buckets[cohortStats.sparkline.buckets.length - 1]?.date}</span>
                    </div>
                  </div>
                  <div className="bg-[#0C0612] p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-mono text-[10px] text-[#6B8E73] uppercase">// Daily revenue</p>
                      <p className="font-mono text-[9px] text-[#4B6B52]">delivered $</p>
                    </div>
                    <MiniSparkline
                      data={cohortStats.sparkline.buckets.map(b => b.revenue)}
                      color="#39FF14"
                      height={36}
                      ariaLabel="Daily delivered revenue"
                    />
                    <div className="flex justify-between mt-1 font-mono text-[9px] text-[#4B6B52]">
                      <span>{cohortStats.sparkline.buckets[0]?.date}</span>
                      <span>peak ${Math.max(...cohortStats.sparkline.buckets.map(b => b.revenue), 0).toFixed(0)}/day</span>
                      <span>{cohortStats.sparkline.buckets[cohortStats.sparkline.buckets.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-[#0C0612] border border-[#2A0F3D]">
            <div className="border-b border-[#2A0F3D] p-3 text-xs uppercase tracking-wider text-[#6B8E73]">
              // {users.length} USER{users.length === 1 ? '' : 'S'}
            </div>
            <ul className="divide-y divide-[#2A0F3D] max-h-[70vh] overflow-y-auto">
              {loadingList && <li className="p-4 text-[#6B8E73]">Loading...</li>}
              {!loadingList && users.length === 0 && <li className="p-4 text-[#6B8E73]">// No users match</li>}
              {users.map(u => (
                <li key={u.id}>
                  <button
                    onClick={() => openUser(u)}
                    className={`w-full text-left p-3 hover:bg-[#2A0F3D] transition-colors ${selectedUser?.id === u.id ? 'bg-[#2A0F3D] border-l-2 border-[#FF9F0A]' : ''}`}
                    data-testid={`user-row-${u.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-[#39FF14] text-sm truncate">{u.name || '(no name)'}</p>
                        <p className="text-[#4B6B52] text-xs truncate">{u.email}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className="rounded-none font-mono text-[10px] border"
                          style={{ borderColor: ROLE_COLORS[u.role] || '#6B8E73', color: ROLE_COLORS[u.role] || '#6B8E73', background: 'transparent' }}>
                          {u.role?.toUpperCase()}
                        </Badge>
                        {u.region && <span className="text-[10px] text-[#6B8E73]">{u.region.toUpperCase()}</span>}
                        {/* Feb 2026 Annual Tier — badge keys on new internal
                            tier names (standard/silver/gold/platinum/onyx).
                            The 'standard' tier is intentionally suppressed so
                            the row list doesn't paint a chip for every rookie. */}
                        {tierMap[u.id] && tierMap[u.id] !== 'standard' && (
                          <span
                            className="text-[9px] font-mono uppercase tracking-wider px-1 border rounded-none"
                            style={(function () {
                              const t = tierMap[u.id];
                              const c = ({
                                onyx:     '#BF5AF2',
                                platinum: '#E5E4E2',
                                gold:     '#FFD700',
                                silver:   '#C0C0C0',
                              })[t] || '#6B8E73';
                              return { color: c, borderColor: c };
                            })()}
                            data-testid={`user-row-tier-${u.id}`}
                          >
                            {({
                              onyx:     'ONYX',
                              platinum: 'PLATINUM',
                              gold:     'GOLD',
                              silver:   'SILVER',
                              standard: 'NEW',
                            })[tierMap[u.id]] || tierMap[u.id]}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ========== RIGHT: Detail + Edit ========== */}
        {/* Phase 6 Tier 1 — on mobile, only show this panel when a user is
            selected. On lg+ screens it always shows (with empty state). */}
        <div className={`space-y-4 ${!selectedUser ? 'hidden lg:block' : ''}`}>
          {/* Mobile-only "← BACK TO LIST" header. Hidden on lg+ where both
              panels are visible side-by-side. */}
          {selectedUser && (
            <div className="flex gap-2 lg:hidden">
              <button
                onClick={backToList}
                className="flex-1 flex items-center justify-center gap-2 text-[#FF9F0A] font-mono text-xs uppercase tracking-wider px-3 py-2 border border-[#FF9F0A]/40 hover:bg-[#FF9F0A]/10 rounded-none"
                data-testid="user-back-to-list-btn"
              >
                ← BACK TO USER LIST
              </button>
              <button
                onClick={openGlobalUserSearch}
                className="flex items-center justify-center gap-1.5 text-[#39FF14] font-mono text-xs uppercase tracking-wider px-3 py-2 border border-[#39FF14]/40 hover:bg-[#39FF14]/10 rounded-none"
                data-testid="user-jump-to-customer-btn-mobile"
                title="Jump to another customer (⌘K)"
              >
                <Search className="w-3.5 h-3.5" /> JUMP →
              </button>
            </div>
          )}
          {/* Desktop-only "Jump to another customer" — sits above the detail panel
              so operators can pivot without going back to the list. Reuses the
              global ⌘K palette. */}
          {selectedUser && (
            <button
              onClick={openGlobalUserSearch}
              className="hidden lg:flex items-center justify-center gap-2 w-full text-[#39FF14] font-mono text-xs uppercase tracking-wider px-3 py-2 border border-[#39FF14]/40 hover:bg-[#39FF14]/10 rounded-none"
              data-testid="user-jump-to-customer-btn"
              title="⌘K / Ctrl+K"
            >
              <Search className="w-3.5 h-3.5" /> Jump to another customer
              <span className="text-[#4B6B52] normal-case tracking-normal">⌘K</span>
            </button>
          )}
          {!selectedUser ? (
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-12 text-center text-[#6B8E73]">
              <UserIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
              // Select a user from the list to edit their profile
            </div>
          ) : !userDetail ? (
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 text-[#6B8E73]" data-testid="user-detail-loading">Loading...</div>
          ) : (
            <>
              <div className="bg-[#0C0612] border border-[#FF9F0A] p-4" data-testid="user-detail-header">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#FF9F0A] text-xs uppercase tracking-wider">// {canEdit ? 'EDITING' : 'VIEWING (READ-ONLY)'}</p>
                    <h2 className="text-[#39FF14] text-xl font-bold mt-1">{userDetail.user.name}</h2>
                    <p className="text-[#6B8E73] text-xs mt-1">{userDetail.user.email}</p>
                    <p className="text-[#4B6B52] text-[10px] mt-1">id: {userDetail.user.id}</p>
                    {!canEdit && (
                      <p className="text-[#6B8E73] text-[10px] mt-2 italic" data-testid="readonly-banner">
                        // Dispatcher view — profile edits, role/region changes, points
                        adjustments, and account recovery are super-admin only.
                      </p>
                    )}
                  </div>
                  {userDetail.loyalty && (
                    <div className="text-right">
                      <p className="text-[10px] text-[#4B6B52] uppercase tracking-wider">// LOYALTY BALANCE</p>
                      <p className="text-[#FF9F0A] text-xl font-bold">{userDetail.loyalty.balance} PTS</p>
                    </div>
                  )}
                </div>
                {/* Feb 2026 — quick "message customer" CTA. Deep-links to the
                    Dispatch dashboard's CHAT tab with the customer preselected.
                    Only surfaces for customer accounts; staff-to-staff DMs are
                    out of scope for the in-app chat feature.

                    Bug fix Feb 2026 — When the drawer opens via `?openUser=<id>`
                    (⌘K palette / Recently-Viewed strip / activity feed),
                    `selectedUser` is a stub without `role`. Prefer the fully
                    hydrated `userDetail.user.role` and only fall back to
                    `selectedUser.role` for the list-click path. */}
                {(userDetail?.user?.role || selectedUser?.role) === 'customer' && (
                  <div className="mt-3 pt-3 border-t border-[#2A0F3D]">
                    <button
                      type="button"
                      onClick={() => window.location.assign(`/dispatch?tab=chat&chat=${selectedUser.id}`)}
                      className="w-full bg-[#B400FF] text-white hover:bg-[#C42BFF] rounded-none font-mono text-xs uppercase tracking-wider py-2 px-3 flex items-center justify-center gap-2"
                      data-testid="user-message-customer-btn"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      MESSAGE CUSTOMER
                    </button>
                  </div>
                )}
              </div>

              {/* Phase 5.9.16 — quick-contact strip + profile meta (signup date,
                  referrer attribution). Lets dispatch tap-to-call / tap-to-text /
                  tap-to-email in one place without copy-paste. */}
              <UserContactStrip user={userDetail.user} referrer={userDetail.referrer} recentOrders={userDetail.recent_orders || []} lifetime={userDetail.lifetime} tierInfo={userDetail.tier} />

              {/* Feb 2026 — PWA install signal. Combines 3 sources (install_bonus
                  ledger entry, standalone auth.login events, push subscription)
                  so dispatch can answer "do they have the app?" without
                  bouncing to /login-stats or /push-health. */}
              {userDetail.pwa && (userDetail.user.role || 'customer') === 'customer' && (
                <PWAInstallBadge pwa={userDetail.pwa} />
              )}

              {/* Feb 2026 — inline loyalty ledger so dispatch can debug
                  "why does this customer have N points?" without bouncing
                  to /admin/points or running Mongo queries. */}
              {userDetail.loyalty_history && userDetail.loyalty_history.length > 0 && (
                <LoyaltyLedgerSection entries={userDetail.loyalty_history} balance={userDetail.loyalty?.balance ?? 0} />
              )}

              {/* Phase 6.0.2 — edit form, account recovery, and danger zone
                  are super-admin only. Dispatchers see read-only profile +
                  audit history above/below. */}
              {canEdit && (<>
              <div className="bg-[#0C0612] border border-[#2A0F3D] p-4 space-y-3" data-testid="user-edit-form">
                <FieldRow label="NAME" testid="field-name">
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="edit-name" />
                </FieldRow>
                <FieldRow label="PHONE" testid="field-phone">
                  <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="edit-phone" />
                </FieldRow>
                <FieldRow label="ADDRESS" testid="field-address">
                  <Input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="edit-address" />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="ROLE" testid="field-role">
                    <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                      <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="edit-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                        {ROLE_OPTIONS.map(r => (
                          <SelectItem key={r} value={r} className="text-[#39FF14] font-mono">{r.toUpperCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="REGION" testid="field-region">
                    <Select value={draft.region || '__none__'} onValueChange={(v) => setDraft({ ...draft, region: v === '__none__' ? '' : v })}>
                      <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="edit-region">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                        <SelectItem value="__none__" className="text-[#39FF14] font-mono">NONE</SelectItem>
                        {regions.map(r => (
                          <SelectItem key={r.code} value={r.code} className="text-[#39FF14] font-mono">{r.code.toUpperCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldRow>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-[#2A0F3D]">
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, active: !draft.active })}
                    className={`px-3 py-1.5 border rounded-none text-xs uppercase tracking-wider flex items-center gap-2 ${
                      draft.active
                        ? 'border-[#39FF14] text-[#39FF14]'
                        : 'border-[#FF3B30] text-[#FF3B30] bg-[rgba(255,59,48,0.06)]'
                    }`}
                    data-testid="edit-active-toggle"
                  >
                    <Power className="w-3 h-3" />
                    {draft.active ? 'ACTIVE' : 'DEACTIVATED'}
                  </button>
                </div>

                {/* Feb 2026 Annual Tier — Executive tier override.
                    Super-admin can pin a customer to any tier for the rest
                    of the annual window. Selecting "AUTO" releases the
                    lock so the tier recomputes from annual order count. */}
                {(userDetail?.user?.role === 'customer' || draft.role === 'customer') && (
                  <div className="bg-[rgba(212,175,55,0.06)] border border-[#B5740E] p-3 mt-2" data-testid="admin-tier-override">
                    <Label className="text-[#FFD60A] text-xs uppercase tracking-wider flex items-center gap-2">
                      // TIER OVERRIDE
                    </Label>
                    <p className="text-[#A3C7A8] font-mono text-[11px] mt-1 mb-2">
                      // Pin this customer to a tier. Defaults to AUTO
                      (recomputes from annual order count). Manual locks
                      expire on Dec 31 unless you set a custom end.
                      {userDetail?.tier?.annual_orders !== undefined && (
                        <> Currently: <span className="text-[#39FF14]">{userDetail.tier.tier_label}</span> · {userDetail.tier.annual_orders} orders YTD.</>
                      )}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <FieldRow label="LOCK TIER">
                        <Select
                          value={draft.locked_tier || '__auto__'}
                          onValueChange={(v) => setDraft({ ...draft, locked_tier: v === '__auto__' ? '' : v })}
                        >
                          <SelectTrigger
                            className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                            data-testid="tier-override-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                            <SelectItem value="__auto__" className="text-[#39FF14] font-mono">AUTO</SelectItem>
                            <SelectItem value="standard" className="text-[#8E8E93] font-mono">NEW</SelectItem>
                            <SelectItem value="silver"   className="text-[#C0C0C0] font-mono">SILVER</SelectItem>
                            <SelectItem value="gold"     className="text-[#FFD700] font-mono">GOLD</SelectItem>
                            <SelectItem value="platinum" className="text-[#E5E4E2] font-mono">PLATINUM</SelectItem>
                            <SelectItem value="onyx"     className="text-[#BF5AF2] font-mono">ONYX</SelectItem>
                          </SelectContent>
                        </Select>
                      </FieldRow>
                      <FieldRow label="LOCK UNTIL">
                        <Input
                          type="datetime-local"
                          value={draft.locked_tier_until ? draft.locked_tier_until.slice(0, 16) : ''}
                          onChange={(e) => setDraft({ ...draft, locked_tier_until: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                          disabled={!draft.locked_tier}
                          placeholder="Dec 31, EOD"
                          className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none disabled:opacity-40"
                          data-testid="tier-override-until"
                        />
                      </FieldRow>
                    </div>
                  </div>
                )}

                {/* Phase 5.5-Lite — Cash App per-driver override (super-admin only, driver users only) */}
                {(userDetail?.user?.role === 'driver' || draft.role === 'driver') && (
                  <div className="bg-[rgba(255,214,10,0.04)] border border-[#FFD60A] p-3 mt-2" data-testid="driver-cashapp-override">
                    <Label className="text-[#FFD60A] text-xs uppercase tracking-wider flex items-center gap-2">
                      // CASH APP OVERRIDE
                    </Label>
                    <p className="text-[#A3C7A8] font-mono text-[11px] mt-1 mb-2">
                      // When set, this driver's pay-on-delivery QRs route here instead of the company default.
                      Leave blank to use the default. Pre-pay (PAY NOW) always uses the company default.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={draft.cashapp_handle ?? (userDetail?.user?.cashapp_handle || '')}
                        onChange={(e) => setDraft({ ...draft, cashapp_handle: e.target.value })}
                        placeholder="$DriverHandle (or blank to use default)"
                        className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none flex-1"
                        data-testid="driver-cashapp-input"
                      />
                      {(draft.cashapp_handle || userDetail?.user?.cashapp_handle) && (
                        <Button
                          type="button"
                          onClick={() => setDraft({ ...draft, cashapp_handle: '' })}
                          variant="outline"
                          size="sm"
                          className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] rounded-none font-mono text-xs"
                          data-testid="driver-cashapp-clear"
                          title="Clear override (uses company default)"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="edit-reason-block">
                <Label className="text-[#6B8E73] text-xs uppercase tracking-wider">
                  REASON * <span className="text-[#4B6B52]">// required for audit</span>
                </Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Fixed typo in address during onboarding call"
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none mt-1"
                  data-testid="edit-reason-input"
                  maxLength={200}
                />
                {diffFields.length > 0 && (
                  <div className="mt-3 text-xs text-[#FFA500]" data-testid="edit-diff-preview">
                    // {diffFields.length} CHANGE{diffFields.length === 1 ? '' : 'S'} PENDING: {diffFields.map(c => c.field).join(', ')}
                  </div>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving || diffFields.length === 0 || !reason.trim()}
                  className="w-full mt-3 bg-[#FF9F0A] text-[#050003] hover:bg-[#FFAF2A] disabled:opacity-40 rounded-none"
                  data-testid="edit-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'SAVING...' : diffFields.length === 0 ? 'NO CHANGES' : 'SAVE CHANGES + AUDIT'}
                </Button>
              </div>
              </>)}

              <div className="bg-[#0C0612] border border-[#2A0F3D] p-4" data-testid="user-audit-history">
                <p className="text-xs uppercase tracking-wider text-[#4B6B52] mb-3 flex items-center gap-2">
                  <History className="w-3 h-3" /> // AUDIT HISTORY ({userDetail.audit.length})
                </p>
                {userDetail.audit.length === 0 ? (
                  <p className="text-[#6B8E73] text-xs">// No prior edits on this account</p>
                ) : (
                  <ul className="divide-y divide-[#2A0F3D] text-xs max-h-96 overflow-y-auto">
                    {userDetail.audit.map((a) => (
                      <li key={a.id} className="py-2" data-testid={`audit-${a.id}`}>
                        <div className="flex items-center justify-between text-[11px]">
                          {/* Phase 5.9.30 — click actor email to jump to that operator's profile */}
                          {a.actor_id ? (
                            <Link
                              to={`/dispatch/admin/users?openUser=${a.actor_id}`}
                              className="text-[#39FF14] hover:underline"
                              data-testid={`audit-actor-link-${a.id}`}
                            >
                              {a.actor_email}
                            </Link>
                          ) : (
                            <span className="text-[#39FF14]">{a.actor_email}</span>
                          )}
                          <span className="text-[#4B6B52] flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(a.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-[#A3C7A8] mt-1 italic">"{a.reason}"</p>
                        <ul className="mt-1 ml-3 list-none">
                          {a.changes.map((c, i) => (
                            <li key={i} className="text-[#6B8E73]">
                              <span className="text-[#FFA500]">{c.field}</span>:{' '}
                              <span className="text-[#FF3B30]">{JSON.stringify(c.from)}</span>
                              {' → '}
                              <span className="text-[#39FF14]">{JSON.stringify(c.to)}</span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Phase 5.9.14 — Account Recovery (super-admin only) */}
              {user?.role === 'super_admin' && selectedUser.id !== user?.id && (
                <div className="bg-[#0C0612] border border-[#FF9F0A] p-4" data-testid="user-account-recovery">
                  <p className="text-[#FF9F0A] text-xs uppercase tracking-wider mb-2">
                    // ACCOUNT RECOVERY
                  </p>
                  <p className="text-[#6B8E73] text-xs mb-3">
                    Reset a forgotten password or fix a typo'd email on behalf of this user. All actions are audit-logged.
                  </p>
                  <AccountRecoveryControls userId={selectedUser.id} userEmail={selectedUser.email} onEmailChanged={(newEmail) => setSelectedUser({...selectedUser, email: newEmail})} />
                </div>
              )}

              {/* Phase 7-lite — Danger Zone: hard-delete user (super-admin only) */}
              {canEdit && selectedUser.role !== 'super_admin' && selectedUser.id !== user?.id && (
                <div className="bg-[#0C0612] border border-[#FF3B30] p-4" data-testid="user-danger-zone">
                  <p className="text-[#FF3B30] text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Trash2 className="w-3 h-3" /> // DANGER ZONE
                  </p>
                  <p className="text-[#6B8E73] text-xs mb-3">
                    Permanently delete this user. Orders are anonymized (revenue history preserved).
                    Points, push subs, audit log, and behavioral events are wiped. <span className="text-[#FF3B30]">Irreversible.</span>
                  </p>
                  {!hardDeletePreview ? (
                    <Button
                      onClick={requestHardDelete}
                      variant="outline"
                      className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] rounded-none font-mono text-xs w-full"
                      data-testid="hard-delete-preview-btn"
                    >
                      <Trash2 className="w-3 h-3 mr-2" />
                      PREVIEW HARD-DELETE
                    </Button>
                  ) : (
                    <div className="space-y-3" data-testid="hard-delete-confirm">
                      <div className="bg-[#050003] border border-[#FF9F0A] p-3 text-[11px] space-y-0.5">
                        <p className="text-[#FF9F0A] mb-1">// CASCADE PREVIEW for {hardDeletePreview.user.email}</p>
                        <p className="text-[#A3C7A8]">– {hardDeletePreview.cascade.orders_to_anonymize} orders → anonymized</p>
                        <p className="text-[#6B8E73]">– {hardDeletePreview.cascade.points_entries_to_delete + hardDeletePreview.cascade.related_points_entries_to_delete} points entries deleted</p>
                        <p className="text-[#6B8E73]">– {hardDeletePreview.cascade.staff_points_entries_to_delete} staff points entries deleted</p>
                        <p className="text-[#6B8E73]">– {hardDeletePreview.cascade.audit_log_entries_to_delete} audit entries deleted</p>
                        <p className="text-[#6B8E73]">– {hardDeletePreview.cascade.push_subs_to_delete} push subscriptions deleted</p>
                        <p className="text-[#6B8E73]">– {hardDeletePreview.cascade.behavioral_events_to_delete} behavioral events deleted</p>
                        <p className="text-[#FF3B30] mt-2">→ User account permanently deleted</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setHardDeletePreview(null)}
                          disabled={hardDeleting}
                          variant="outline"
                          className="flex-1 border-[#2A0F3D] text-[#6B8E73] rounded-none font-mono text-xs"
                          data-testid="hard-delete-cancel-btn"
                        >
                          CANCEL
                        </Button>
                        <Button
                          onClick={confirmHardDelete}
                          disabled={hardDeleting}
                          className="flex-1 bg-[#FF3B30] text-white hover:bg-[#FF5040] rounded-none font-mono text-xs"
                          data-testid="hard-delete-confirm-btn"
                        >
                          {hardDeleting ? 'DELETING...' : 'CONFIRM PERMANENT DELETE'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[#6B8E73] text-xs uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * Phase 5.9.16 — Tap-to-call / tap-to-text / tap-to-email actions + profile
 * metadata (signup date, referrer attribution) for the SA user detail panel.
 *
 * Phone digits are normalized to a `tel:` and `sms:` href (we strip everything
 * except digits + leading `+`). Empty fields render disabled-looking chips so
 * the operator immediately sees "no phone on file" without confusion.
 */

/**
 * Feb 2026 — PWA install badge for the User Management drawer.
 *
 * Renders 3 chips so dispatch can answer "do they have the app?" at a glance:
 *   • Install status (green ✓ when any signal positive, red ✗ otherwise)
 *   • Last standalone login timestamp (if any)
 *   • Push subscription presence (bell on/off)
 *
 * The combined signal is overdetermined by design (3 independent sources) so
 * even if one drops a record, the badge still fires on the other two.
 */
function PWAInstallBadge({ pwa }) {
  const installed = !!pwa.installed;
  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };
  return (
    <div
      className="bg-[#0C0612] border border-[#2A0F3D] p-3 flex items-center gap-3 flex-wrap"
      data-testid="user-pwa-badge"
    >
      <Smartphone className={installed ? 'w-4 h-4 text-[#39FF14]' : 'w-4 h-4 text-[#6B8E73]'} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#6B8E73]">// PWA INSTALL</div>
        <div className="text-sm font-mono mt-0.5">
          {installed ? (
            <span className="text-[#39FF14]">✓ Installed</span>
          ) : (
            <span className="text-[#82937B]">✗ Not installed</span>
          )}
          {installed && (
            <span className="text-[#A3C7A8] text-xs ml-2" data-testid="user-pwa-last-login">
              · last opened {fmt(pwa.last_standalone_at) || '—'}
            </span>
          )}
        </div>
        {installed && (
          <div className="text-[10px] text-[#4B6B52] font-mono mt-0.5">
            since {fmt(pwa.first_standalone_at)} ·
            {' '}{pwa.standalone_login_count} standalone login{pwa.standalone_login_count === 1 ? '' : 's'}
            {pwa.install_bonus_awarded && (
              <span className="ml-2 text-[#FF9F0A]">· 🎁 100pt bonus paid</span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0" title={pwa.has_push_subscription ? 'Has push subscription' : 'No push subscription'}>
        {pwa.has_push_subscription ? (
          <Bell className="w-3.5 h-3.5 text-[#00F0FF]" />
        ) : (
          <BellOff className="w-3.5 h-3.5 text-[#4B6B52]" />
        )}
        <span className={`text-[10px] uppercase tracking-widest font-mono ${pwa.has_push_subscription ? 'text-[#00F0FF]' : 'text-[#4B6B52]'}`}>
          {pwa.has_push_subscription ? 'push on' : 'no push'}
        </span>
      </div>
    </div>
  );
}

function UserContactStrip({ user, referrer, recentOrders = [], lifetime = null, tierInfo = null }) {
  const phoneRaw = (user.phone || '').trim();
  const phoneTel = phoneRaw.replace(/[^\d+]/g, '');
  const hasPhone = phoneTel.length >= 7;
  const email = (user.email || '').trim();
  const signupIso = user.created_at || null;
  const signupLabel = signupIso
    ? new Date(signupIso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  const baseChip = "flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs uppercase tracking-wider rounded-none border transition-colors";
  const enabledChip = "border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)]";
  const disabledChip = "border-[#2A0F3D] text-[#4B6B52] cursor-not-allowed";

  return (
    <div className="bg-[#0C0612] border border-[#2A0F3D] p-3 space-y-3" data-testid="user-contact-strip">
      {/* Quick-action chips */}
      <div className="flex flex-wrap gap-2">
        {hasPhone ? (
          <>
            <a
              href={`tel:${phoneTel}`}
              data-testid="user-action-call"
              className={`${baseChip} ${enabledChip}`}
            >
              <Phone className="w-3 h-3" /> CALL
            </a>
            <a
              href={`sms:${phoneTel}`}
              data-testid="user-action-sms"
              className={`${baseChip} ${enabledChip}`}
              // Phase 5.9.17 will replace this with the in-app SMS composer
              // (Twilio). Tap-to-text via the device's default app keeps the
              // workflow unblocked until then.
              title="Opens your device's default SMS app (in-app messaging coming soon)"
            >
              <MessageSquare className="w-3 h-3" /> TEXT
            </a>
          </>
        ) : (
          <span className={`${baseChip} ${disabledChip}`} data-testid="user-action-no-phone">
            <Phone className="w-3 h-3" /> NO PHONE ON FILE
          </span>
        )}
        {email ? (
          <a
            href={`mailto:${email}`}
            data-testid="user-action-email"
            className={`${baseChip} ${enabledChip}`}
            // Phase 5.9.17 will surface the in-app templated email composer
            // (Resend). `mailto:` keeps the operator unblocked until then.
            title="Opens your device's default mail app (in-app send coming soon)"
          >
            <Mail className="w-3 h-3" /> EMAIL
          </a>
        ) : null}
      </div>

      {/* Profile metadata */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-2 border-t border-[#2A0F3D]">
        <div className="flex items-center gap-2 text-xs font-mono">
          <CalendarDays className="w-3.5 h-3.5 text-[#6B8E73] flex-shrink-0" />
          <span className="text-[#6B8E73] uppercase tracking-wider">// signed up</span>
          <span className="text-[#A3C7A8]" data-testid="user-signup-date">
            {signupLabel || <em className="text-[#4B6B52]">unknown</em>}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono">
          <UserPlus className="w-3.5 h-3.5 text-[#6B8E73] flex-shrink-0" />
          <span className="text-[#6B8E73] uppercase tracking-wider">// referred by</span>
          {referrer ? (
            referrer.id ? (
              // Feb 2026 — clickable so dispatch can jump into the referrer's
              // profile from the referred customer's drawer without going back
              // to the user list. Falls back to a plain span for legacy rows
              // where the referrer id is missing.
              <Link
                to={`/dispatch/admin/users?openUser=${referrer.id}&from=referrer`}
                className="text-[#A3C7A8] truncate hover:underline"
                data-testid="user-referred-by-link"
              >
                <span className="text-[#39FF14]">{referrer.name || referrer.email}</span>
                {referrer.role && (
                  <span className="text-[#6B8E73] ml-1">· {referrer.role}</span>
                )}
              </Link>
            ) : (
              <span className="text-[#A3C7A8] truncate" data-testid="user-referred-by">
                <span className="text-[#39FF14]">{referrer.name || referrer.email}</span>
                {referrer.role && (
                  <span className="text-[#6B8E73] ml-1">· {referrer.role}</span>
                )}
              </span>
            )
          ) : (
            <span className="text-[#4B6B52]" data-testid="user-referred-by-none"><em>organic signup</em></span>
          )}
        </div>
      </div>

      {/* Phase 5.9.16 — Recent orders mini-strip (Customer 360 in one place). */}
      <RecentOrdersStrip orders={recentOrders} lifetime={lifetime} tierInfo={tierInfo} />
    </div>
  );
}

function statusColor(status) {
  switch (status) {
    case 'pending': return '#FF9F0A';
    case 'confirmed': return '#00F0FF';
    case 'in_transit':
    case 'out_for_delivery': return '#FFD60A';
    case 'delivered': return '#39FF14';
    case 'cancelled': return '#FF3B30';
    default: return '#A3C7A8';
  }
}

function LtvLine({ lifetime, vipTier }) {
  if (!lifetime) return null;
  const spent = Number(lifetime.spent || 0);
  const n = Number(lifetime.orders || 0);
  const aov = Number(lifetime.aov || 0);
  const cancelled = Number(lifetime.cancelled || 0);
  if (n === 0 && cancelled === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono mb-2 pb-2 border-b border-dashed border-[#2A0F3D]"
      data-testid="user-ltv-line"
    >
      <span className="text-[#6B8E73] uppercase tracking-wider">// LTV</span>
      <span className="text-[#39FF14] font-bold" data-testid="user-ltv-spent">${Number(spent ?? 0).toFixed(2)}</span>
      <span className="text-[#A3C7A8]" data-testid="user-ltv-orders">
        {n} order{n === 1 ? '' : 's'}
      </span>
      {n > 0 && (
        <span className="text-[#6B8E73]" data-testid="user-ltv-aov">
          · ${Number(aov ?? 0).toFixed(2)} AOV
        </span>
      )}
      {cancelled > 0 && (
        <span className="text-[#FF3B30]" data-testid="user-ltv-cancelled">
          · {cancelled} cancelled
        </span>
      )}
      {vipTier && (
        <span
          className="ml-auto px-1.5 py-0.5 border text-[10px] uppercase tracking-wider"
          style={{ color: vipTier.color, borderColor: vipTier.color }}
          data-testid="user-ltv-tier"
        >
          {vipTier.label}
        </span>
      )}
    </div>
  );
}

function RecentOrdersStrip({ orders, lifetime, tierInfo = null }) {
  // Feb 2026 Annual Tier — the recent-orders strip shows the user's
  // EFFECTIVE tier (backend-resolved, honours locked_tier +
  // tier_lock_until). Aug 2026 hotfix: previously computed the badge
  // from YTD order-count only — this silently ignored grandfathered
  // pilots whose YTD count hadn't caught up to their locked tier.
  // Symptom: Chris Klenk (10 orders YTD, locked_tier=onyx) rendered as
  // GOLD instead of ONYX. Now: trust tierInfo.effective_tier from the
  // /admin/users/{id} response's `tier` field.
  const TIER_COLOR = {
    onyx:     '#BF5AF2',
    platinum: '#E5E4E2',
    gold:     '#FFD700',
    silver:   '#C0C0C0',
    standard: null,  // no badge for standard/new
  };
  const TIER_LABEL = {
    onyx: 'ONYX', platinum: 'PLATINUM', gold: 'GOLD',
    silver: 'SILVER', standard: null,
  };
  const ytdOrders = Number(lifetime?.orders_ytd ?? lifetime?.orders ?? 0);
  const effective = tierInfo?.effective_tier || tierInfo?.tier;
  let vipTier = null;
  if (effective && TIER_COLOR[effective] && TIER_LABEL[effective]) {
    vipTier = { label: TIER_LABEL[effective], color: TIER_COLOR[effective] };
  } else {
    // Legacy fallback for pre-payload responses (tierInfo missing) — reads
    // straight from YTD order count. Deploy-window safety net.
    vipTier =
        ytdOrders >= 50 ? { label: 'ONYX',     color: '#BF5AF2' }
      : ytdOrders >= 25 ? { label: 'PLATINUM', color: '#E5E4E2' }
      : ytdOrders >= 10 ? { label: 'GOLD',     color: '#FFD700' }
      : ytdOrders >=  3 ? { label: 'SILVER',   color: '#C0C0C0' }
      : null;
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="pt-2 border-t border-[#2A0F3D]" data-testid="user-recent-orders-empty">
        <LtvLine lifetime={lifetime} vipTier={vipTier} />
        <div className="flex items-center gap-2 text-xs font-mono">
          <Package className="w-3.5 h-3.5 text-[#6B8E73] flex-shrink-0" />
          <span className="text-[#6B8E73] uppercase tracking-wider">// recent orders</span>
          <span className="text-[#4B6B52]"><em>no orders yet</em></span>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-2 border-t border-[#2A0F3D]" data-testid="user-recent-orders">
      <LtvLine lifetime={lifetime} vipTier={vipTier} />
      <div className="flex items-center gap-2 text-xs font-mono mb-2">
        <Package className="w-3.5 h-3.5 text-[#6B8E73] flex-shrink-0" />
        <span className="text-[#6B8E73] uppercase tracking-wider">// recent orders</span>
        <span className="text-[#4B6B52]">({orders.length})</span>
      </div>
      <div className="space-y-1">
        {orders.map((o) => {
          const dateLabel = o.created_at
            ? new Date(o.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : '—';
          // Feb 2026 — canonicalise on `slice(0, 8).toUpperCase()` to match
          // DispatchDashboard, loyalty.py reason strings ("Order delivered
          // #ABC12345"), and points-ledger links. Previously used last-6-upper
          // which produced a totally different short id (#952F9B vs #D26F7DFD)
          // for the exact same order — operators couldn't cross-reference the
          // Recent Orders strip against the Points Ledger. Same order everywhere now.
          const shortId = (o.id || '').slice(0, 8).toUpperCase();
          const color = statusColor(o.status);
          return (
            <Link
              key={o.id}
              to={`/dispatch?orderId=${o.id}`}
              className="flex items-center gap-2 px-2 py-1.5 border border-[#2A0F3D] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.04)] transition-colors text-xs font-mono"
              data-testid={`user-recent-order-${o.id}`}
              title={`Order #${shortId} · ${o.status}`}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: color }}
                aria-hidden="true"
              />
              <span className="text-[#A3C7A8] flex-shrink-0">#{shortId}</span>
              <span className="uppercase tracking-wider flex-shrink-0" style={{ color }}>
                {(o.status || '').replace(/_/g, ' ')}
              </span>
              <span className="text-[#6B8E73] flex-1 truncate text-right">
                {o.items_count} item{o.items_count === 1 ? '' : 's'} · ${(o.total || 0).toFixed(2)}
              </span>
              <span className="text-[#4B6B52] flex-shrink-0">{dateLabel}</span>
              {o.region && (
                <span className="text-[#4B6B52] uppercase flex-shrink-0">{o.region}</span>
              )}
              {/* Feb 2026 — Delivery Health chip on the SA audit surface.
                  Only renders for orders that have a computed score
                  (out_for_delivery/delivered with pings). Color-coded so ops
                  can eyeball a bad-tracking pattern per customer. */}
              {o.delivery_health && (
                <span
                  className={`flex-shrink-0 px-1.5 py-0 font-mono text-[10px] uppercase tracking-wider ${
                    o.delivery_health.status === 'healthy'
                      ? 'text-[#39FF14] bg-[#39FF14]/10'
                      : o.delivery_health.status === 'degraded'
                        ? 'text-[#FF9F0A] bg-[#FF9F0A]/10'
                        : 'text-[#FF3366] bg-[#FF3366]/10'
                  }`}
                  title={`GPS coverage · ${o.delivery_health.ping_count} pings · ${o.delivery_health.catch_up_count} wake-catchups`}
                  data-testid={`user-recent-order-health-${o.id}`}
                >
                  {o.delivery_health.score}%
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default UsersAdminPage;

// ---------------------------------------------------------------------------
// LoyaltyLedgerSection — Feb 2026
//
// Inline ledger viewer inside the user drawer. Renders the 50 most recent
// `points_ledger` entries returned by GET /api/admin/users/{id} so dispatch
// can audit "why this balance?" without leaving the drawer.
//
// Designed to be visually quieter than the audit log (loyalty events are
// noisier — every delivered order, every redemption — so we collapse by
// default and surface only the last 5 in the closed state).
// ---------------------------------------------------------------------------

const LEDGER_SOURCE_COLORS = {
  earn:                  '#39FF14',
  redeem:                '#FF9F0A',
  referral_bonus:        '#00F0FF',
  qr_signup_bonus:       '#00F0FF',
  admin_adjust:          '#BF5AF2',
  transfer_in:           '#00F0FF',
  transfer_out:          '#FF9F0A',
  cancellation_reversal: '#FF3B30',
  earn_reversal:         '#FF3B30',
  install_bonus:         '#FFD60A',
  install_bonus_retro:   '#FFD60A',
  founders_thanks:       '#FFD60A',
  staff_qr_signup:       '#00F0FF',
  staff_qr_first_order:  '#00F0FF',
};

function LoyaltyLedgerSection({ entries, balance }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? entries : entries.slice(0, 5);
  const hasMore = entries.length > 5;
  // Sanity check: sum of deltas should match the balance (helps spot drift).
  const ledgerSum = entries.reduce((acc, e) => acc + (Number(e.delta) || 0), 0);
  const driftWarn = entries.length >= 50 ? null : (ledgerSum !== balance ? (balance - ledgerSum) : null);

  return (
    <div className="bg-[#0C0612] border border-[#FF9F0A]/40 p-4" data-testid="user-loyalty-ledger">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs uppercase tracking-wider text-[#FF9F0A]">// POINTS LEDGER ({entries.length})</p>
        <div className="text-right">
          <p className="text-[10px] text-[#4B6B52] uppercase tracking-wider">// SUM Δ</p>
          <p className="text-[#FF9F0A] font-mono text-sm" data-testid="ledger-sum-delta">
            {ledgerSum >= 0 ? '+' : ''}{ledgerSum}
          </p>
        </div>
      </div>
      {driftWarn !== null && (
        <p className="text-[10px] text-[#FF3B30] font-mono mb-2" data-testid="ledger-drift-warn">
          // DRIFT — balance is {balance}, ledger sums to {ledgerSum} (Δ {driftWarn > 0 ? '+' : ''}{driftWarn})
        </p>
      )}
      <ul className="divide-y divide-[#2A0F3D] text-xs">
        {shown.map((e) => {
          const color = LEDGER_SOURCE_COLORS[e.source] || '#A3C7A8';
          const delta = Number(e.delta) || 0;
          const ts = e.created_at ? new Date(e.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
          return (
            <li key={e.id} className="py-2 flex items-start gap-3" data-testid={`ledger-row-${e.id}`}>
              <span
                className="font-mono text-sm shrink-0 w-14 text-right tabular-nums"
                style={{ color: delta >= 0 ? '#39FF14' : '#FF3B30' }}
                data-testid={`ledger-delta-${e.id}`}
              >
                {delta >= 0 ? '+' : ''}{delta}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider" style={{ color }} data-testid={`ledger-source-${e.id}`}>
                    {e.source || 'unknown'}
                  </span>
                  {e.region && <span className="text-[10px] text-[#4B6B52] uppercase">{e.region}</span>}
                  <span className="text-[10px] text-[#4B6B52] ml-auto">{ts}</span>
                </div>
                <p className="text-[#A3C7A8] text-[11px] mt-0.5 break-words">{e.reason || ''}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#6B8E73]">
                  {e.order_id && (
                    <Link
                      to={`/dispatch/orders?focus=${e.order_id}`}
                      className="text-[#00F0FF] hover:underline"
                      data-testid={`ledger-order-link-${e.id}`}
                    >
                      order #{e.order_id.slice(0, 8).toUpperCase()}
                    </Link>
                  )}
                  {e.related_user_id && (
                    <Link
                      to={`/dispatch/admin/users?openUser=${e.related_user_id}`}
                      className="text-[#00F0FF] hover:underline"
                      data-testid={`ledger-related-link-${e.id}`}
                    >
                      → related user
                    </Link>
                  )}
                  {e.actor && <span>by {e.actor}</span>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-[10px] text-[#FF9F0A] hover:underline uppercase tracking-wider"
          data-testid="ledger-toggle-expand"
        >
          {expanded ? `// COLLAPSE` : `// SHOW ALL ${entries.length} ENTRIES`}
        </button>
      )}
    </div>
  );
}
